import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { X, Play, Pause, RotateCcw, Volume2, VolumeX, Home } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { StepperInput } from './StepperInput';
import { Label } from '@/components/ui/label';
import { playMetronomeTick } from '@/utils/audioUtils';
import ExerciseCopyright from './ExerciseCopyright';
import ExerciseTopLogo from './ExerciseTopLogo';

const CustomExerciseModal = ({ isOpen, onClose, pattern }) => {
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [phase, setPhase] = useState('ready');
  const [progress, setProgress] = useState(0);
  const [cyclesCompleted, setCyclesCompleted] = useState(0);
  const [sessionDuration, setSessionDuration] = useState(5);
  const [remainingTime, setRemainingTime] = useState(5 * 60);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const intervalRef = useRef(null);
  const timerRef = useRef(null);
  const soundEnabledRef = useRef(true);

  // Get timings from pattern
  const timings = {
    inhale: pattern?.inhale_duration || 4,
    'hold-in': pattern?.hold_after_inhale || 0,
    exhale: pattern?.exhale_duration || 4,
    'hold-out': pattern?.hold_after_exhale || 0
  };

  // Determine which phases are active (duration > 0)
  const activePhases = ['inhale', 'hold-in', 'exhale', 'hold-out'].filter(p => timings[p] > 0);

  // Phase colors - matching capsule style (solid colors)
  const phaseColors = useMemo(() => ({
    inhale: '#22d3ee',      // Cyan
    'hold-in': '#a855f7',   // Violet
    exhale: '#3b82f6',      // Blue
    'hold-out': '#a855f7',  // Violet
    ready: '#94a3b8'
  }), []);

  // Calculate total cycle duration
  const totalCycleDuration = useMemo(() => {
    return activePhases.reduce((sum, p) => sum + timings[p], 0);
  }, [activePhases, timings]);

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  useEffect(() => {
    if (!isActive) {
      setRemainingTime(sessionDuration * 60);
    }
  }, [sessionDuration, isActive]);

  const getNextPhase = (currentPhase) => {
    const currentIndex = activePhases.indexOf(currentPhase);
    const nextIndex = (currentIndex + 1) % activePhases.length;
    return activePhases[nextIndex];
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const getPhaseLabel = () => {
    switch (phase) {
      case 'inhale': return 'Inspirez';
      case 'hold-in': return 'Retenez';
      case 'exhale': return 'Expirez';
      case 'hold-out': return 'Retenez';
      default: return 'Prêt';
    }
  };

  const startExercise = () => {
    setIsActive(true);
    setIsPaused(false);
    setPhase(activePhases[0]);
    setProgress(0);
    setCyclesCompleted(0);
    setRemainingTime(sessionDuration * 60);
  };

  const stopExercise = () => {
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setProgress(0);
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  // Main breathing cycle - only metronome sound, no voice
  useEffect(() => {
    if (!isActive || isPaused) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    const phaseDuration = timings[phase] || 4;
    const intervalTime = 50;
    const increment = (100 / (phaseDuration * 1000)) * intervalTime;

    intervalRef.current = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + increment;
        
        if (newProgress >= 100) {
          const nextPhase = getNextPhase(phase);
          setPhase(nextPhase);
          
          // No sound here - sound is handled by the session timer (once per second)
          
          if (nextPhase === activePhases[0]) {
            setCyclesCompleted(c => c + 1);
          }
          
          return 0;
        }
        return newProgress;
      });
    }, intervalTime);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isActive, isPaused, phase, timings]);

  // Session timer - tick every second
  useEffect(() => {
    if (!isActive || isPaused) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      // Play metronome tick every second
      if (soundEnabledRef.current) {
        playMetronomeTick();
      }
      
      setRemainingTime(prev => {
        if (prev <= 1) {
          stopExercise();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isActive, isPaused]);

  // Cleanup on unmount or close
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      stopExercise();
    }
  }, [isOpen]);

  // Wave path calculation - dynamically calculated based on phase durations
  // SVG width: 640, height: 400
  // Curves take fixed space for visual consistency, plateaus are proportional to hold duration
  
  // Calculate segment positions based on active phases
  const segmentPositions = useMemo(() => {
    const totalWidth = 640;
    const curveWidth = 120; // Fixed width for inhale/exhale curves
    
    // Calculate total duration for proportional distribution
    const holdInDuration = timings['hold-in'] || 0;
    const holdOutDuration = timings['hold-out'] || 0;
    const totalHoldDuration = holdInDuration + holdOutDuration;
    
    // Available width after curves
    const availableForHolds = totalWidth - (2 * curveWidth);
    
    let positions = {};
    let currentX = 0;
    
    // Inhale curve (always present)
    positions.inhale = {
      startX: currentX,
      endX: currentX + curveWidth,
      type: 'curve-up'
    };
    currentX += curveWidth;
    
    // Hold-in plateau (only if duration > 0)
    if (holdInDuration > 0) {
      const holdInWidth = totalHoldDuration > 0 
        ? (holdInDuration / totalHoldDuration) * availableForHolds 
        : availableForHolds / 2;
      positions['hold-in'] = {
        startX: currentX,
        endX: currentX + holdInWidth,
        type: 'plateau-top'
      };
      currentX += holdInWidth;
    }
    
    // Exhale curve (always present)
    positions.exhale = {
      startX: currentX,
      endX: currentX + curveWidth,
      type: 'curve-down'
    };
    currentX += curveWidth;
    
    // Hold-out plateau (only if duration > 0)
    if (holdOutDuration > 0) {
      positions['hold-out'] = {
        startX: currentX,
        endX: totalWidth,
        type: 'plateau-bottom'
      };
    }
    
    return positions;
  }, [timings]);

  // Generate SVG wave path dynamically
  const wavePath = useMemo(() => {
    const inhale = segmentPositions.inhale;
    const holdIn = segmentPositions['hold-in'];
    const exhale = segmentPositions.exhale;
    const holdOut = segmentPositions['hold-out'];
    
    if (!inhale || !exhale) return 'M 0 320 L 640 320';
    
    let path = `M ${inhale.startX} 320`;
    
    // Inhale curve (bottom to top)
    const inhaleControlX = inhale.startX + (inhale.endX - inhale.startX) / 2;
    path += ` Q ${inhaleControlX} 320 ${inhale.endX} 120`;
    
    // Hold-in plateau (if present)
    if (holdIn) {
      path += ` L ${holdIn.endX} 120`;
    }
    
    // Exhale curve (top to bottom)
    const exhaleControlX = exhale.startX + (exhale.endX - exhale.startX) / 2;
    path += ` Q ${exhaleControlX} 120 ${exhale.endX} 320`;
    
    // Hold-out plateau (if present)
    if (holdOut) {
      path += ` L ${holdOut.endX} 320`;
    }
    
    return path;
  }, [segmentPositions]);

  // Get wave position based on phase and progress
  const getWavePosition = useCallback(() => {
    if (phase === 'ready' || !isActive) return { x: 0, y: 320 };
    
    const seg = segmentPositions[phase];
    if (!seg) return { x: 0, y: 320 };
    
    const t = progress / 100; // 0 to 1
    const segWidth = seg.endX - seg.startX;
    
    let x, y;
    
    if (seg.type === 'curve-up') {
      // Quadratic bezier from bottom to top
      const controlX = seg.startX + segWidth / 2;
      x = (1-t)*(1-t)*seg.startX + 2*(1-t)*t*controlX + t*t*seg.endX;
      y = (1-t)*(1-t)*320 + 2*(1-t)*t*320 + t*t*120;
    } else if (seg.type === 'plateau-top') {
      // Linear at top
      x = seg.startX + t * segWidth;
      y = 120;
    } else if (seg.type === 'curve-down') {
      // Quadratic bezier from top to bottom
      const controlX = seg.startX + segWidth / 2;
      x = (1-t)*(1-t)*seg.startX + 2*(1-t)*t*controlX + t*t*seg.endX;
      y = (1-t)*(1-t)*120 + 2*(1-t)*t*120 + t*t*320;
    } else {
      // Linear at bottom (hold-out)
      x = seg.startX + t * segWidth;
      y = 320;
    }
    
    return { x, y };
  }, [phase, progress, isActive, segmentPositions]);

  const wavePos = getWavePosition();
  const currentColor = phaseColors[phase] || phaseColors.ready;

  // Remaining time for current phase
  const phaseRemainingTime = phase !== 'ready' 
    ? Math.ceil((1 - progress / 100) * (timings[phase] || 0))
    : null;

  if (!isOpen || !pattern) return null;

  return (
    <div 
      className="fixed inset-0 z-[60] flex flex-col"
      style={{ 
        background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 50%, #1e1b4b 100%)'
      }}
    >
      <ExerciseCopyright />
      {/* Floating Navigation Buttons */}
      <div className="absolute top-4 left-4 z-20">
        <button
          onClick={() => {
            stopExercise();
            onClose();
          }}
          className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
          title="Retour aux exercices"
        >
          <Home size={20} className="text-white" />
        </button>
      </div>
      
      <div className="absolute top-4 right-4 z-20">
        <button
          onClick={() => {
            stopExercise();
            onClose();
          }}
          className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
          title="Fermer"
        >
          <X size={20} className="text-white" />
        </button>
      </div>

      <ExerciseTopLogo />

      {/* Main Content */}
      <div className="relative flex-1 overflow-y-auto px-4 pt-20 sm:pt-24 pb-40 flex flex-col lg:flex-row lg:items-center lg:justify-center lg:pb-8">
        <div className="flex flex-col items-center my-auto gap-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-10 lg:items-center w-full">
          
          {/* Settings Panel - Show when not active OR when paused */}
          <div className="w-full max-w-xs lg:w-64 order-2 lg:order-none lg:col-start-1 lg:row-start-1 lg:justify-self-end">
            {/* Timer moved from the top-center overlay to a stats card to keep the
                header clean (matches the ReduceFrequencyModal layout). */}
            <div className="bg-white/85 backdrop-blur-md rounded-xl px-3 py-3 text-center border border-slate-300 shadow-md mb-4">
              <p className="text-slate-500 text-xs mb-1">Temps restant</p>
              <p className="text-slate-800 font-bold text-2xl" style={{ fontFamily: 'system-ui' }}>
                {formatTime(remainingTime)}
              </p>
            </div>
            {(!isActive || isPaused) && (
              <div className="bg-black/40 backdrop-blur-md rounded-2xl p-5 shadow-xl border border-white/10">
                <h3 className="font-semibold text-white mb-4">
                  {isPaused ? 'Pause - Réglages' : 'Réglages'}
                </h3>
                
                <div className="space-y-5">
                  <div>
                    <Label className="text-sm text-white/80 mb-2 block">Durée</Label>
                    <StepperInput
                      value={sessionDuration}
                      onChange={(v) => {
                        setSessionDuration(v);
                        if (isPaused) setRemainingTime(v * 60);
                      }}
                      min={1}
                      max={30}
                      step={1}
                      unit="min"
                      label="durée"
                      colorClass="violet"
                      valueTestId="custom-duration-value"
                      incTestId="custom-duration-inc"
                      decTestId="custom-duration-dec"
                    />
                  </div>

                  <div className="pt-3 border-t border-white/20 text-xs text-white/60 space-y-1">
                    <p className="font-medium text-white/80 mb-2">Phases :</p>
                    <p style={{color: phaseColors.inhale}}>● Inspiration : {timings.inhale}s</p>
                    {timings['hold-in'] > 0 && <p style={{color: phaseColors['hold-in']}}>● Rétention : {timings['hold-in']}s</p>}
                    <p style={{color: phaseColors.exhale}}>● Expiration : {timings.exhale}s</p>
                    {timings['hold-out'] > 0 && <p style={{color: phaseColors['hold-out']}}>● Rétention : {timings['hold-out']}s</p>}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Center - Wave Visualization */}
          <div className="flex flex-col items-center order-1 lg:order-none lg:col-start-2 lg:row-start-1">
            
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 text-center drop-shadow-lg">
              {pattern.name}
            </h2>

            {/* Wave Container */}
            <div 
              className="relative rounded-3xl overflow-hidden shadow-2xl"
              style={{
                width: '640px',
                height: '400px',
                background: 'linear-gradient(180deg, rgba(15,23,42,0.9) 0%, rgba(30,58,95,0.8) 100%)',
                backdropFilter: 'blur(20px)',
                border: '2px solid rgba(255,255,255,0.15)',
                boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.1), 0 20px 60px rgba(0,0,0,0.5)'
              }}
            >
              <svg 
                viewBox="0 0 640 400" 
                className="absolute inset-0 w-full h-full"
                preserveAspectRatio="none"
              >
                <defs>
                  {/* Solid colors for each phase - no gradients */}
                  
                  {/* Glass overlay for depth effect */}
                  <linearGradient id="glassOverlay" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.2)" />
                    <stop offset="50%" stopColor="rgba(255,255,255,0.05)" />
                    <stop offset="100%" stopColor="rgba(0,0,0,0.1)" />
                  </linearGradient>
                  
                  {/* Ball gradient - simple */}
                  <radialGradient id="ballGrad" cx="30%" cy="30%">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
                    <stop offset="50%" stopColor={currentColor} stopOpacity="0.95" />
                    <stop offset="100%" stopColor={currentColor} stopOpacity="0.8" />
                  </radialGradient>
                  
                  {/* Ball glow filter */}
                  <filter id="ballGlow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="4" result="blur"/>
                    <feMerge>
                      <feMergeNode in="blur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                  
                  {/* Progress clip */}
                  <clipPath id="progressClip">
                    <rect x="0" y="0" width={wavePos.x} height="400" />
                  </clipPath>
                </defs>
                
                {/* Background wave path - dynamic */}
                <path
                  d={wavePath}
                  fill="none"
                  stroke="rgba(255,255,255,0.15)"
                  strokeWidth="3"
                />
                
                {/* Filled segments with progress - dynamic based on active phases */}
                <g clipPath="url(#progressClip)">
                  {/* Inhale segment - always present */}
                  {segmentPositions.inhale && (() => {
                    const seg = segmentPositions.inhale;
                    const controlX = seg.startX + (seg.endX - seg.startX) / 2;
                    const path = `M ${seg.startX} 320 Q ${controlX} 320 ${seg.endX} 120 L ${seg.endX} 400 L ${seg.startX} 400 Z`;
                    return (
                      <>
                        <path d={path} fill={phaseColors.inhale} fillOpacity="0.85" />
                        <path d={path} fill="url(#glassOverlay)" />
                      </>
                    );
                  })()}
                  
                  {/* Hold-in segment - only if duration > 0 */}
                  {segmentPositions['hold-in'] && (() => {
                    const seg = segmentPositions['hold-in'];
                    const path = `M ${seg.startX} 120 L ${seg.endX} 120 L ${seg.endX} 400 L ${seg.startX} 400 Z`;
                    return (
                      <>
                        <path d={path} fill={phaseColors['hold-in']} fillOpacity="0.85" />
                        <path d={path} fill="url(#glassOverlay)" />
                      </>
                    );
                  })()}
                  
                  {/* Exhale segment - always present */}
                  {segmentPositions.exhale && (() => {
                    const seg = segmentPositions.exhale;
                    const controlX = seg.startX + (seg.endX - seg.startX) / 2;
                    const path = `M ${seg.startX} 120 Q ${controlX} 120 ${seg.endX} 320 L ${seg.endX} 400 L ${seg.startX} 400 Z`;
                    return (
                      <>
                        <path d={path} fill={phaseColors.exhale} fillOpacity="0.85" />
                        <path d={path} fill="url(#glassOverlay)" />
                      </>
                    );
                  })()}
                  
                  {/* Hold-out segment - only if duration > 0 */}
                  {segmentPositions['hold-out'] && (() => {
                    const seg = segmentPositions['hold-out'];
                    const path = `M ${seg.startX} 320 L ${seg.endX} 320 L ${seg.endX} 400 L ${seg.startX} 400 Z`;
                    return (
                      <>
                        <path d={path} fill={phaseColors['hold-out']} fillOpacity="0.85" />
                        <path d={path} fill="url(#glassOverlay)" />
                      </>
                    );
                  })()}
                  
                  {/* Shine line on top of fill - dynamic */}
                  <path
                    d={wavePath.replace(/320/g, '322').replace(/120/g, '122')}
                    fill="none"
                    stroke="rgba(255,255,255,0.5)"
                    strokeWidth="2"
                  />
                  
                  {/* Bubbles inside fill */}
                  {[...Array(8)].map((_, i) => (
                    <circle
                      key={i}
                      cx={40 + i * 75}
                      cy={350 - (i % 3) * 50}
                      r={3 + (i % 3)}
                      fill="rgba(255,255,255,0.3)"
                      className="animate-pulse"
                      style={{ animationDelay: `${i * 0.2}s` }}
                    />
                  ))}
                </g>
                
                {/* Ball indicator - follows the wave line exactly */}
                <g 
                  transform={`translate(${wavePos.x}, ${wavePos.y})`}
                  filter="url(#ballGlow)"
                  style={{ transition: 'transform 0.08s linear' }}
                >
                  {/* Outer glow */}
                  <circle r="28" fill={currentColor} opacity="0.3" />
                  {/* Main ball */}
                  <circle r="18" fill="url(#ballGrad)" />
                  {/* Shine */}
                  <ellipse cx="-4" cy="-5" rx="8" ry="6" fill="rgba(255,255,255,0.7)" />
                  {/* Highlight dot */}
                  <circle cx="-5" cy="-7" r="3" fill="rgba(255,255,255,0.95)" />
                </g>
              </svg>
              
              {/* Phase labels */}
              <div className="absolute bottom-3 left-0 right-0 flex justify-around text-sm px-6">
                <span className={`transition-all duration-300 ${phase === 'inhale' ? 'font-bold scale-110' : 'text-white/50'}`}
                      style={{ color: phase === 'inhale' ? phaseColors.inhale : undefined }}>
                  Inspirez
                </span>
                {timings['hold-in'] > 0 && (
                  <span className={`transition-all duration-300 ${phase === 'hold-in' ? 'font-bold scale-110' : 'text-white/50'}`}
                        style={{ color: phase === 'hold-in' ? phaseColors['hold-in'] : undefined }}>
                    Retenez
                  </span>
                )}
                <span className={`transition-all duration-300 ${phase === 'exhale' ? 'font-bold scale-110' : 'text-white/50'}`}
                      style={{ color: phase === 'exhale' ? phaseColors.exhale : undefined }}>
                  Expirez
                </span>
                {timings['hold-out'] > 0 && (
                  <span className={`transition-all duration-300 ${phase === 'hold-out' ? 'font-bold scale-110' : 'text-white/50'}`}
                        style={{ color: phase === 'hold-out' ? phaseColors['hold-out'] : undefined }}>
                    Retenez
                  </span>
                )}
              </div>
            </div>

            {/* Phase indicator */}
            <div className="text-center mt-6">
              <span 
                className="text-4xl sm:text-5xl font-bold drop-shadow-lg transition-colors duration-300"
                style={{ color: currentColor }}
              >
                {getPhaseLabel()}
              </span>
              {phaseRemainingTime !== null && (
                <span className="text-2xl sm:text-3xl text-white/70 ml-3">
                  {phaseRemainingTime}s
                </span>
              )}
            </div>

            {/* Cycles counter */}
            <div className="mt-4 bg-white/10 backdrop-blur rounded-xl px-4 py-2">
              <span className="text-lg font-medium text-white">{cyclesCompleted}</span>
              <span className="text-white/60 ml-2">cycles</span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-row lg:flex-col items-center gap-3 order-3 lg:order-none lg:col-start-3 lg:row-start-1 lg:justify-self-start exercise-controls-mobile-fixed">
            {/* Sound Toggle (metronome only) */}
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="w-12 h-12 rounded-full bg-white/10 backdrop-blur flex items-center justify-center hover:bg-white/20 transition-colors"
              title="Métronome"
            >
              {soundEnabled ? (
                <Volume2 size={20} className="text-white" />
              ) : (
                <VolumeX size={20} className="text-white" />
              )}
            </button>

            {/* Play/Pause */}
            <button
              onClick={() => {
                if (!isActive) {
                  startExercise();
                } else {
                  setIsPaused(!isPaused);
                }
              }}
              className="w-16 h-16 rounded-full bg-white/10 backdrop-blur flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              {!isActive || isPaused ? (
                <Play size={28} className="text-white ml-1" />
              ) : (
                <Pause size={28} className="text-white" />
              )}
            </button>

            {/* Reset */}
            {isActive && (
              <button
                onClick={stopExercise}
                className="w-12 h-12 rounded-full bg-white/10 backdrop-blur flex items-center justify-center hover:bg-white/20 transition-colors"
                title="Réinitialiser"
              >
                <RotateCcw size={20} className="text-white" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomExerciseModal;
